'use client';

import { ReactNode, useState, useCallback, useMemo } from 'react';
import { reduxLogger } from '@/core/utils/logger';
import { TourContext } from './TourContext';
import type { TourContextValue, TourDefinition } from '../types';

interface TourProviderProps {
  tours: TourDefinition[];
  children: ReactNode;
}

/**
 * Generic TourProvider — manages state for any registered tour.
 *
 * Call `startTour(tourId)` to activate a tour by its registry ID.
 * `completeTour()` resets all state (no backend logic — tour-specific
 * overlays handle their own side effects before calling completeTour).
 */
export function TourProvider({ tours, children }: TourProviderProps) {
  const [activeTourId, setActiveTourId] = useState<string | null>(null);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [completedSteps, setCompletedSteps] = useState<Set<string>>(new Set());

  const activeTour = activeTourId ? tours.find((t) => t.id === activeTourId) : undefined;
  const isActive = !!activeTour;
  const currentStep = activeTour ? (activeTour.steps[currentStepIndex] ?? null) : null;

  const startTour = useCallback(
    (tourId: string, startIndex?: number) => {
      const tour = tours.find((t) => t.id === tourId);
      if (!tour) {
        reduxLogger.warn(`[Tour] Unknown tour ID: ${tourId}`);
        return;
      }
      setActiveTourId(tourId);
      setCurrentStepIndex(startIndex ?? 0);
      setCompletedSteps(new Set());
      reduxLogger.info(`[Tour] Started tour: ${tourId}${startIndex ? ` at step ${startIndex}` : ''}`);
    },
    [tours],
  );

  const nextStep = useCallback(() => {
    if (!activeTour) return;
    setCurrentStepIndex((prev) => {
      const currentId = activeTour.steps[prev]?.id;
      if (currentId) {
        setCompletedSteps((s) => {
          const next = new Set(s);
          next.add(currentId);
          return next;
        });
      }
      const nextIndex = prev + 1;
      if (nextIndex < activeTour.steps.length) {
        reduxLogger.info(`[Tour] Advancing to step ${nextIndex}: ${activeTour.steps[nextIndex].id}`);
        return nextIndex;
      }
      return prev;
    });
  }, [activeTour]);

  const completeTour = useCallback(() => {
    if (!activeTourId) return;
    reduxLogger.info(`[Tour] Completed tour: ${activeTourId}`);
    setActiveTourId(null);
    setCurrentStepIndex(0);
    setCompletedSteps(new Set());
  }, [activeTourId]);

  const value = useMemo<TourContextValue>(
    () => ({
      isActive,
      activeTourId,
      currentStepIndex,
      currentStep,
      completedSteps,
      nextStep,
      completeTour,
      startTour,
    }),
    [isActive, activeTourId, currentStepIndex, currentStep, completedSteps, nextStep, completeTour, startTour],
  );

  return <TourContext.Provider value={value}>{children}</TourContext.Provider>;
}
